/* eslint-disable import/prefer-default-export, consistent-return */
import { useEffect, useRef, useState } from 'react';
import { notificationsService } from 'network-utils';
import { useQuery, useQueryClient } from 'react-query';
import eventBus from '@services/eventBus';

const NOTIFICATION_TYPES = ['openTournamentChanged', 'hasNewNotifications'];
const notificationsApi = notificationsService.NotificationsApi;

export const getHasNewNotificationsKey = 'has-new-notifications';
export const getNotificationsKey = 'fetch-notifications';

export const useGetHasNewNotifications = (token: string) => {
    return useQuery([getHasNewNotificationsKey, token], () => notificationsApi.getHasNewNotifications(token), {
        enabled: !!token,
        keepPreviousData: true
    });
};

export const useGetNotifications = (token: string, first: number, offset: number) => {
    return useQuery(
        [getNotificationsKey, first, offset, token],
        () => notificationsApi.getNotificationsByUuid(token, first, offset),
        {
            keepPreviousData: true,
            cacheTime: 0,
            enabled: !!token
        }
    );
};

export const useNotificationsWebsocket = (token: string) => {
    const websocketRef = useRef<WebSocket | null>(null);
    const [waitingToReconnect, setWaitingToReconnect] = useState(false);
    const firstConnection = useRef(true);
    const connectionRetries = useRef(0);

    useEffect(() => {
        if (!token) return;

        if (!waitingToReconnect && !websocketRef.current) {
            const websocket = new WebSocket(`${process.env.CB_NOTIFICATIONS_SERVICE_SERVER_WS_URL}/websockets`);
            websocketRef.current = websocket;

            websocket.onopen = () => {
                websocket.send(JSON.stringify({ type: 'auth', value: token }));

                if (!firstConnection.current) {
                    eventBus.dispatch('notification', { type: 'reconnected' });
                }
                firstConnection.current = false;
                connectionRetries.current = 0;
            };

            websocket.onmessage = (event: any) => {
                const data = JSON.parse(event.data);
                if (NOTIFICATION_TYPES.includes(data.type)) {
                    eventBus.dispatch('notification', data);
                }
            };

            websocket.onclose = () => {
                connectionRetries.current++;
                if (connectionRetries.current > 5) return;

                setWaitingToReconnect(true);
                setTimeout(() => setWaitingToReconnect(false), 5000);
            };
        }

        return () => {
            websocketRef.current?.close();
            websocketRef.current = null;
        };
    }, [token, waitingToReconnect]);
};
